import { Box, CircularProgress, Grid } from '@mui/material';
import { IProfileData, StreamDataPointKeys } from '../../types/api/profile';
import { GridCard } from '../Card/Card';
import AdpMassFlowMeterLC from '../AdpMassFlowMeter/AdpMassFlowMeter';
import { useTranslation } from 'react-i18next';
import { LIVE_SESSION_NS } from '../BunkeringDashboard/i18n';
import { NAVIGATION_NS } from '../../i18n/navigation';
import { RIGHT_DRAWER_NS } from './i18n';
import { CenteredTypographyBox } from '../Aggregators/CenteredTypographyBox';
import { HorizontalScale } from '../HorizontalScale/HorizontalScale';
import { LineChart } from '../LineChart/LineChart';
import { HubsNames } from '../../stores/useSignalR';
import { DeliverySummary } from './DeliverySummary';
import { useMessageStore } from '../../stores/useMessageStore';
import { BunkeringData } from './types';
import { isEmerson } from '../../utils/stringUtils';
import React, { useMemo } from 'react';
import shallow from 'zustand/shallow';

type BunkeringOverviewProps = {
  data: BunkeringData | Record<string, never>;
};

const loaderBlock = (
  <Box sx={{ display: 'flex', justifyContent: 'center', padding: '0.5em' }}>
    <CircularProgress />
  </Box>
);

export default function BunkeringOverview(props: BunkeringOverviewProps) {
  const { t } = useTranslation([LIVE_SESSION_NS, NAVIGATION_NS, RIGHT_DRAWER_NS]);
  const { messages: aggragatedData = [], loading: isAggregationLoading } = useMessageStore(
    (state) => ({
      messages: state.messages[HubsNames.aggregateOverview],
      loading: state.stableLoadingFlags[HubsNames.aggregateOverview],
    }),
    shallow,
  );
  const { messages: deliveryProfile = [], loading: isLoadingDeliveryProfile } = useMessageStore(
    (state) => ({
      messages: state.messages[HubsNames.bunkeringOverview] as IProfileData[],
      loading: state.stableLoadingFlags[HubsNames.bunkeringOverview],
    }),
    shallow,
  );
  const isEmersonDevice = useMemo(
    () => isEmerson(props.data?.deviceInfo?.name),
    [props.data.deviceInfo],
  );

  return (
    <Box>
      <Box component="section" sx={{ marginBottom: '8px' }}>
        <Grid item xs={12} md={6} sx={{ mb: 2 }}>
          <DeliverySummary data={props.data} />
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <GridCard title={t('components/BunkeringDashboard:massTotal')}>
              {isAggregationLoading || !aggragatedData[0] ? (
                loaderBlock
              ) : (
                <CenteredTypographyBox
                  unit={aggragatedData[0].massTotal.unit}
                  value={aggragatedData[0].massTotal.cumulative}
                />
              )}
            </GridCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <GridCard title={t('components/BunkeringDashboard:density')}>
              {isAggregationLoading || !aggragatedData[0] ? (
                loaderBlock
              ) : (
                <HorizontalScale
                  min={aggragatedData[0].density.range.warning.min}
                  max={aggragatedData[0].density.range.warning.max}
                  step={200}
                  average={aggragatedData[0].density.aggregated}
                  instant={aggragatedData[0].density.instant}
                  unit={aggragatedData[0].density.unit}
                  showInstant={false}
                />
              )}
            </GridCard>
          </Grid>
          <Grid item xs={12} md={4}>
            {isEmersonDevice ? (
              <GridCard title={t('components/BunkeringDashboard:driveGainAeration')}>
                {isAggregationLoading || !aggragatedData[0] ? (
                  loaderBlock
                ) : (
                  <HorizontalScale
                    min={aggragatedData[0].driveGain.range.warning.min}
                    max={aggragatedData[0].driveGain.range.warning.max}
                    step={25}
                    average={aggragatedData[0].driveGain.aggregated}
                    instant={aggragatedData[0].driveGain.instant}
                    unit={aggragatedData[0].driveGain.unit}
                    showInstant={false}
                  />
                )}
              </GridCard>
            ) : (
              <GridCard title={t('components/BunkeringDashboard:tubeDamping')}>
                {isAggregationLoading || !aggragatedData[0] ? (
                  loaderBlock
                ) : (
                  <HorizontalScale
                    min={0}
                    max={10}
                    step={1}
                    average={0}
                    instant={0}
                    unit={''}
                    showInstant={true}
                  />
                )}
              </GridCard>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <GridCard title={t('components/BunkeringDashboard:temperature')}>
              {isAggregationLoading || !aggragatedData[0] ? (
                loaderBlock
              ) : (
                <HorizontalScale
                  min={aggragatedData[0].temperature.range.warning.min}
                  max={aggragatedData[0].temperature.range.warning.max}
                  step={5}
                  average={aggragatedData[0].temperature.aggregated}
                  instant={aggragatedData[0].temperature.instant}
                  unit={aggragatedData[0].temperature.unit}
                  showInstant={false}
                />
              )}
            </GridCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <GridCard title={t('components/BunkeringDashboard:volumeTotal')}>
              {isAggregationLoading || !aggragatedData[0] ? (
                loaderBlock
              ) : (
                <CenteredTypographyBox
                  unit={aggragatedData[0].volumeTotal.unit}
                  value={aggragatedData[0].volumeTotal.cumulative}
                />
              )}
            </GridCard>
          </Grid>
        </Grid>
      </Box>
      <Box component="section" sx={{ marginBottom: '8px' }}>
        <Grid container spacing={1}>
          <Grid item md={12} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:mfmReadings')} fullscreen={true}>
              <AdpMassFlowMeterLC
                isEmerson={isEmersonDevice}
                loading={isLoadingDeliveryProfile}
                values={deliveryProfile}
              />
            </GridCard>
          </Grid>
        </Grid>
      </Box>
      <Box component="section">
        <Grid container spacing={1}>
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:massFlowRate')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.massFlowRate}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} MT/hour`;
                      },
                    },
                  },
                }}
                data={deliveryProfile}
                loading={isLoadingDeliveryProfile}
              />
            </GridCard>
          </Grid>
          {isEmersonDevice && (
            <Grid item sm={6} xs={12}>
              <GridCard title={t('components/BunkeringDashboard:volumeFlowRate')} fullscreen={true}>
                <LineChart
                  dataKey={StreamDataPointKeys.volumeFlowRate}
                  options={{
                    yAxis: {
                      title: {
                        text: undefined,
                      },
                      labels: {
                        formatter: function () {
                          return `${this.value} m³/hour`;
                        },
                      },
                    },
                  }}
                  data={deliveryProfile}
                  loading={isLoadingDeliveryProfile}
                />
              </GridCard>
            </Grid>
          )}
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:massTotal')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.massTotal}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} MT`;
                      },
                    },
                  },
                }}
                data={deliveryProfile}
                loading={isLoadingDeliveryProfile}
              />
            </GridCard>
          </Grid>
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:volumeTotal')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.volumeTotal}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} m³`;
                      },
                    },
                  },
                }}
                data={deliveryProfile}
                loading={isLoadingDeliveryProfile}
              />
            </GridCard>
          </Grid>
          <Grid item sm={6} xs={12}>
            {isEmersonDevice ? (
              <GridCard title={t('components/BunkeringDashboard:driveGain')} fullscreen={true}>
                <LineChart
                  dataKey={StreamDataPointKeys.driveGain}
                  options={{
                    yAxis: {
                      title: {
                        text: undefined,
                      },
                      labels: {
                        formatter: function () {
                          return `${this.value} %`;
                        },
                      },
                    },
                  }}
                  data={deliveryProfile}
                  loading={isLoadingDeliveryProfile}
                />
              </GridCard>
            ) : (
              <GridCard title={t('components/BunkeringDashboard:tubeDamping')} fullscreen={true}>
                <LineChart
                  dataKey={StreamDataPointKeys.tubeDamping}
                  options={{
                    yAxis: {
                      title: {
                        text: undefined,
                      },
                    },
                  }}
                  data={deliveryProfile}
                  loading={isLoadingDeliveryProfile}
                />
              </GridCard>
            )}
          </Grid>
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:density')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.density}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} kg/m³`;
                      },
                    },
                  },
                }}
                data={deliveryProfile}
                loading={isLoadingDeliveryProfile}
              />
            </GridCard>
          </Grid>
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:temperature')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.temperature}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} °C`;
                      },
                    },
                  },
                }}
                data={deliveryProfile}
                loading={isLoadingDeliveryProfile}
              />
            </GridCard>
          </Grid>
          <Grid item sm={6} xs={12}>
            <GridCard title={t('components/BunkeringDashboard:pressure')} fullscreen={true}>
              <LineChart
                dataKey={StreamDataPointKeys.pressure}
                options={{
                  yAxis: {
                    title: {
                      text: undefined,
                    },
                    labels: {
                      formatter: function () {
                        return `${this.value} bar`;
                      },
                    },
                  },
                }}
                data={deliveryProfile}
                loading={isLoadingDeliveryProfile}
              />
            </GridCard>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
